import { FunctionComponent, createContext, useCallback, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { fetchCloningErrorsExitRulesQuery } from 'modules/journey/components/ExitRules/api/exitRulesQueries';
import { useInitialClonedRulesErrorCheck } from 'modules/journey/components/RuleBuilder/hooks';
import { getWorkflow } from 'modules/workflows/ducks';
import { WorkflowType } from 'modules/workflows/enums';
import { useFetchExitRules } from 'modules/journey/components/ExitRules/hooks/useFetchExitRules';
import { Rule } from 'modules/journey/components/RuleBuilder/types';

type ExitRuleContextState = {
  exitRules: Rule[];
  allExitRules: Rule[];
  getExitRules: () => void;
  loadingExitRules: boolean;
  exitRuleCount: number;
  exitRuleCountError: boolean;
  hasCloningErrors: boolean;
  getExitRuleCriteria: () => void;
};

const ExitRuleContext = createContext<ExitRuleContextState>({
  exitRules: [],
  allExitRules: [],
  getExitRules: () => undefined,
  loadingExitRules: false,
  exitRuleCount: 0,
  exitRuleCountError: false,
  hasCloningErrors: false,
  getExitRuleCriteria: () => undefined,
});

export const ExitRuleContextProvider: FunctionComponent<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const workflow = useSelector(getWorkflow);

  const {
    exitRules,
    allExitRules,
    isLoading,
    refetch: refetchExitrules,
    error,
  } = useFetchExitRules();

  const [fetchCloningErrorsExitRules, { data: CloningErrorsExitRulesQueryData }] = useLazyQuery(
    fetchCloningErrorsExitRulesQuery,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const exitCriteriaFn = useCallback(() => {
    refetchExitrules({
      workflowId: workflow.id,
      workflowType: workflow.workflowType,
      includeArchived: false,
    });
    if (workflow.workflowType !== WorkflowType.PUBLISHED) {
      fetchCloningErrorsExitRules({
        variables: {
          workflowId: workflow.id,
          workflowType: workflow.workflowType,
          includeArchived: false,
        },
      });
    }
  }, [workflow.id, workflow.workflowType]);

  useEffect(() => {
    if (workflow?.id) {
      fetchCloningErrorsExitRules({
        variables: {
          workflowId: workflow.id,
          workflowType: workflow.workflowType,
          includeArchived: false,
        },
      });
    }
  }, [workflow.id, workflow.workflowType]);

  const cloningError = useInitialClonedRulesErrorCheck(
    CloningErrorsExitRulesQueryData?.workflow?.exitRules || [],
  );

  const exitRuleCount = exitRules?.length || 0;

  const context: ExitRuleContextState = {
    exitRules,
    allExitRules,
    getExitRules: refetchExitrules,
    loadingExitRules: isLoading,
    exitRuleCount,
    exitRuleCountError: !!error,
    getExitRuleCriteria: exitCriteriaFn,
    hasCloningErrors: cloningError,
  };
  return <ExitRuleContext.Provider value={context}>{children}</ExitRuleContext.Provider>;
};

export const useExitRuleContext = () => useContext(ExitRuleContext);
